import { getExtraHeaders } from "@/lib/queryClient";

/**
 * Shared course vocabulary for the admin course manager (/admin/courses) and the
 * creator course builder (/creator/courses). Shapes follow scripts/courses/API_CONTRACT.md.
 */

export type CourseStatus = "draft" | "pending_review" | "changes_requested" | "published" | "archived";
export type CourseCategory = "school" | "interest";
export type CourseLevel = "starter" | "explorer" | "expert";

export const COURSE_STATUSES: CourseStatus[] = [
  "draft",
  "pending_review",
  "changes_requested",
  "published",
  "archived",
];

export const COURSE_STATUS_META: Record<CourseStatus, { label: string; className: string; blurb: string }> = {
  draft: {
    label: "Draft",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    blurb: "Students cannot see this course yet. Keep editing until it is ready.",
  },
  pending_review: {
    label: "Pending review",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    blurb: "Waiting on the Academy team. Editing is paused until the review is done.",
  },
  changes_requested: {
    label: "Changes requested",
    className: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    blurb: "The team asked for a few fixes. Read the note, update the course, then submit again.",
  },
  published: {
    label: "Published",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    blurb: "Live for students. They can enrol and earn the credential.",
  },
  archived: {
    label: "Archived",
    className: "bg-muted text-muted-foreground",
    blurb: "Hidden from students. Existing enrolments stay on record.",
  },
};

export const COURSE_LEVELS: { id: CourseLevel; label: string; hint: string }[] = [
  { id: "starter", label: "Starter", hint: "New to the subject" },
  { id: "explorer", label: "Explorer", hint: "Knows the basics" },
  { id: "expert", label: "Expert", hint: "Ready for a challenge" },
];

/** Fixed interest taxonomy. Ids are stored on the course, labels are display only. */
export const COURSE_INTEREST_TAGS: { id: string; label: string; emoji: string }[] = [
  { id: "coding", label: "Coding", emoji: "💻" },
  { id: "robotics-engineering", label: "Robotics and Engineering", emoji: "🤖" },
  { id: "space", label: "Space", emoji: "🚀" },
  { id: "animals-nature", label: "Animals and Nature", emoji: "🦜" },
  { id: "environment", label: "Environment", emoji: "🌱" },
  { id: "art-design", label: "Art and Design", emoji: "🎨" },
  { id: "music", label: "Music", emoji: "🎵" },
  { id: "storytelling-writing", label: "Storytelling and Writing", emoji: "📖" },
  { id: "business-money", label: "Business and Money", emoji: "💰" },
  { id: "cooking-food", label: "Cooking and Food", emoji: "🍲" },
  { id: "sports-body", label: "Sports and Body", emoji: "⚽" },
  { id: "jamaican-history-culture", label: "Jamaican History and Culture", emoji: "🇯🇲" },
  { id: "world-cultures-languages", label: "World Cultures and Languages", emoji: "🌍" },
  { id: "video-media", label: "Video and Media", emoji: "🎬" },
  { id: "maths-puzzles", label: "Maths and Puzzles", emoji: "🧩" },
  { id: "science-experiments", label: "Science Experiments", emoji: "🔬" },
  { id: "health-wellbeing", label: "Health and Wellbeing", emoji: "💚" },
  { id: "careers", label: "Careers", emoji: "🧑‍🏫" },
];

const TAG_LABELS = new Map(COURSE_INTEREST_TAGS.map((t) => [t.id, t.label]));

export function interestTagLabel(id: string): string {
  return TAG_LABELS.get(id) || id.replace(/-/g, " ");
}

export interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  emoji: string | null;
  coverImageUrl: string | null;
  category: CourseCategory;
  interestTags: string[];
  subject: string | null;
  gradeMin: number | null;
  gradeMax: number | null;
  level: CourseLevel;
  estimatedMinutes: number;
  lessonCount: number;
  xpTotal: number;
  credentialTitle: string;
  status: CourseStatus;
  creatorName: string | null;
  prerequisites: string[];
  sortOrder: number;
  publishedAt: string | null;
  /** Set by an admin when a submitted course needs work. Not on every payload. */
  reviewNote?: string | null;
}

export interface CourseModuleRow {
  id: string;
  sortOrder: number;
  required: boolean;
  lesson: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    emoji: string | null;
    estimatedMinutes: number;
    xpReward: number;
  };
}

/** GET /api/courses/:slug, used to read a course's current, ordered module list. */
export interface CourseDetail {
  course: Course;
  modules: CourseModuleRow[];
}

/** Slugify a title the same way the lesson pipeline does. */
export function slugifyTitle(input: string): string {
  return input
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 140);
}

/** Fetch helper that surfaces the server's own error text verbatim. */
export async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: getExtraHeaders() });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* not JSON, keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Pull the most useful message out of whatever an apiRequest rejection carries. */
export function errorMessage(error: unknown, fallback = "Something went wrong"): string {
  const e = error as any;
  return e?.response?.error || e?.response?.message || e?.message || fallback;
}
